/**
 * Client-side "Similar To Me" check, mirroring the discover page's Option C
 * smooth-scaling overlap threshold (see DiscoverService.fetchSimilarInterests).
 *
 * Two users are "similar" when the number of shared tags (identities + interests)
 * meets a required overlap that scales with the smaller of the two tag counts:
 *
 *   smallerPool   = min(viewerTagCount, targetTagCount)
 *   smoothFactor  = 0.20 + 0.05 × smallerPool
 *   requiredOverlap = max(2, ceil(smallerPool × smoothFactor))
 *   similar       = sharedTagCount >= requiredOverlap
 *
 * Computing this from the two profiles keeps the badge and gold cost consistent
 * with discover, and works anywhere (profiles, cards) without needing the
 * discover session pool to have been fetched.
 */

type TagUser = { identities?: any[]; interests?: any[] } | null | undefined;

function tagsOf(u: TagUser): string[] {
  return [...(u?.identities ?? []), ...(u?.interests ?? [])]
    .map((t) => String(t).trim().toLowerCase())
    .filter(Boolean);
}

export function getSharedTagCount(viewer: TagUser, target: TagUser): number {
  const viewerSet = new Set(tagsOf(viewer));
  const counted = new Set<string>();
  let shared = 0;
  for (const t of tagsOf(target)) {
    if (viewerSet.has(t) && !counted.has(t)) {
      shared++;
      counted.add(t);
    }
  }
  return shared;
}

export function isSimilarToMe(viewer: TagUser, target: TagUser): boolean {
  if (!viewer || !target) return false;
  const viewerCount = tagsOf(viewer).length;
  const targetCount = tagsOf(target).length;
  const smallerPool = Math.min(viewerCount, targetCount);
  if (smallerPool === 0) return false;
  const shared = getSharedTagCount(viewer, target);
  const smoothFactor = 0.20 + 0.05 * smallerPool;
  const requiredOverlap = Math.max(2, Math.ceil(smallerPool * smoothFactor));
  return shared >= requiredOverlap;
}
